if(!customElements.get('payment-terms-container')) {
  /**
   * Custom element that manages Shopify payment terms display for product variants.
   * Listens for variant changes and dynamically fetches/updates payment terms content.
   *
   * @class PaymentTermsContainer
   * @extends HTMLElement
   *
   * @example
   * <payment-terms-container class="debug">
   *   <!-- Payment terms content will be dynamically inserted here -->
   * </payment-terms-container>
   */
  class PaymentTermsContainer extends HTMLElement {
    /**
     * Creates an instance of PaymentTermsContainer.
     *
     * @constructor
     */
    constructor() {
      super();

      /** @type {HTMLElement|null} Reference to the parent product-overview element */
      this.productOverview = null;

      /** @type {HTMLElement|null} Reference to the current shopify-payment-terms element */
      this.shopifyPaymentTerms = null;

      /** @type {boolean} Current loading state */
      this.isLoading = false;

      /** @type {AbortController|null} Controller for cancelling fetch requests */
      this.fetchController = null;

      /** @type {string} Cache key for the last fetched variant to prevent duplicate requests */
      this.lastFetchKey = '';

      // Bind event handlers once to prevent memory leaks
      this.boundHandleVariantChange = this.handleVariantChange.bind(this);
      this.boundHandleSubmit = this.#handleSubmit.bind(this);
    }

    /**
     * Checks if debug mode is enabled by looking for 'debug' class.
     *
     * @returns {boolean} True if debug mode is enabled
     */
    allowDebug() {
      return this.classList.contains('debug');
    }

    /**
     * Sets the loading state and updates the UI accordingly.
     * Prevents unnecessary DOM updates if state hasn't changed.
     *
     * @param {boolean} loading - The new loading state
     */
    setLoadingState(loading) {
      if (this.isLoading === loading) return;

      this.isLoading = loading;

      if (loading) {
        this.classList.add('loading');
      } else {
        this.classList.remove('loading');
      }
    }

    /**
     * Handles variant change events from the product overview component.
     * Validates the event source and updates payment terms accordingly.
     *
     * @param {CustomEvent} event - The variant change event
     * @param {Object} event.detail - Event details
     * @param {HTMLElement} event.detail.productOverview - The product overview element
     * @returns {Promise<void>}
     */
    async handleVariantChange(event) {
      const { productOverview } = event.detail;
      if (!productOverview) {
        if (this.allowDebug()) {
          console.log('PaymentTermsContainer: No product overview found');
        }
        return;
      }

      // Ensure the product overview is the same as the one we're listening to
      if (!productOverview.isEqualNode(this.productOverview)) {
        if (this.allowDebug()) {
          console.log('PaymentTermsContainer: Product overview is not the same as the one we\'re listening to');
        }
        return;
      }

      // Update availability state based on shopify and greyson availability.
      // only show the payment terms if greyson wants the variant surfaced AND the variant can be purchased on shopify
      if (productOverview.variant.available_shopify && productOverview.variant.available_greyson) {
        this.classList.remove('variant-unavailable');
      } else {
        // otherwise hide the payment terms, as an "add to cart" button would not be visible in this state.
        this.classList.add('variant-unavailable');
      }

      // If we have existing payment terms for the same product, just update the variant ID
      if (this.shopifyPaymentTerms &&
          this.shopifyPaymentTerms.getAttribute('product-handle') === productOverview.variant.product_handle) {
        if (this.allowDebug()) {
          console.log('PaymentTermsContainer: Updating variant id', productOverview.variant.id);
        }
        this.updateVariantId(productOverview.variant.id);
        return;
      }

      // Fetch new payment terms for different product or first load
      if (this.allowDebug()) {
        console.log('PaymentTermsContainer: Fetching payment terms', productOverview.variant.product_handle, productOverview.variant.id);
      }
      this.fetchPaymentTerms(productOverview.variant.product_handle, productOverview.variant.id);
    }

    /**
     * Fetches payment terms HTML for a specific product variant.
     * Includes request deduplication and timeout handling for performance.
     *
     * @param {string} [productHandle=''] - The product handle
     * @param {number} [variantId=0] - The variant ID
     * @returns {Promise<void>}
     */
    async fetchPaymentTerms(productHandle = '', variantId = 0) {
      // Normalize inputs
      productHandle = productHandle.toString();
      variantId = parseInt(variantId, 10);

      if (productHandle.trim().length === 0) {
        this.setLoadingState(false);
        return;
      }

      // Create cache key to prevent duplicate requests
      const fetchKey = `${productHandle}-${variantId}`;
      if (this.lastFetchKey === fetchKey && this.isLoading) {
        if (this.allowDebug()) {
          console.log('PaymentTermsContainer: Skipping duplicate request for', fetchKey);
        }
        return;
      }

      // Cancel any existing request
      if (this.fetchController) {
        this.fetchController.abort();
      }

      // Create new abort controller for this request
      this.fetchController = new AbortController();
      this.lastFetchKey = fetchKey;
      this.setLoadingState(true);

      // Build the fetch URL
      const url = new URL(`/products/${productHandle}`, window.location.origin);
      const params = new URLSearchParams();

      // Add variant ID if valid, otherwise Shopify will default to the first variant
      if (!isNaN(variantId)) {
        params.set('variant', variantId);
      }

      params.set('sections', 'product-payment-terms');
      url.search = params.toString();

      const variantUrl = url.pathname + url.search;

      if (this.allowDebug()) {
        console.log('PaymentTermsContainer: Fetching payment terms from:', variantUrl);
      }

      try {
        const response = await fetch(variantUrl, {
          signal: this.fetchController.signal
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        const json = await response.json();
        // shopify section rendering API returns an object with a key of the section name, and a value of the HTML string
        const paymentTerms = json['product-payment-terms'];

        if (this.allowDebug()) {
          console.log('PaymentTermsContainer: Payment terms:', paymentTerms);
        }

        // Parse and process the HTML response
        this.#processPaymentTermsHTML(paymentTerms, productHandle, variantId);

      } catch (err) {
        // Don't log abort errors as they're intentional
        if (err.name !== 'AbortError') {
          console.error('PaymentTermsContainer: Fetch error:', err);
        }
      } finally {
        this.setLoadingState(false);
        this.fetchController = null;
      }
    }

    /**
     * Processes the fetched payment terms HTML and updates the DOM.
     *
     * @private
     * @param {string} paymentTermsHTML - The HTML string containing payment terms
     * @param {string} productHandle - The product handle
     * @param {number} variantId - The variant ID
     */
    #processPaymentTermsHTML(paymentTermsHTML, productHandle, variantId) {
      if (!paymentTermsHTML) {
        return;
      }

      // Parse the HTML string
      const parser = new DOMParser();
      const doc = parser.parseFromString(paymentTermsHTML, 'text/html');
      // `form | payment_terms` should return with a form element and a shopify-payment-terms element inside it.
      const form = doc.querySelector('form');
      const shopifyPaymentTerms = form?.querySelector('shopify-payment-terms');

      if (form && shopifyPaymentTerms) {
        // Remove form attributes to prevent accidental submissions
        form.removeAttribute('action');
        form.removeAttribute('method');

        // Set attributes for the payment terms element
        // product-handle is custom for our own use, to detect for child product changes
        shopifyPaymentTerms.setAttribute('product-handle', productHandle);
        // variant-id is already present and set by shopify, we just want to make sure it's set to what we need.
        shopifyPaymentTerms.setAttribute('variant-id', variantId);

        // Replace content and update reference
        this.replaceChildren(form);
        this.shopifyPaymentTerms = shopifyPaymentTerms;

        // Brief delay to allow Shopify's payment terms to initialize
        setTimeout(() => {
          if (!this.isLoading) return;
          this.setLoadingState(false);
        }, 300);
      }
    }

    /**
     * Updates the variant ID on the existing shopify-payment-terms element.
     * This triggers Shopify's internal update mechanism.
     *
     * @param {number} variantId - The new variant ID
     */
    updateVariantId(variantId) {
      if (!this.shopifyPaymentTerms) return;

      // shopify-payment-terms will automatically update when variant-id changes if the variant data is in the component's dataset it renders with.
      this.shopifyPaymentTerms.setAttribute('variant-id', variantId);
    }

    /**
     * Handles form submit events to prevent accidental cart additions.
     * This is a workaround since Shopify payment terms require a form context.
     *
     * @private
     * @param {Event} event - The submit event
     */
    #handleSubmit(event) {
      event.preventDefault();
      event.stopPropagation();
    }

    /**
     * Sets up event listeners for the component.
     *
     * @private
     */
    #setEventListeners() {
      document.addEventListener('product-overview:variant-change', this.boundHandleVariantChange);
      this.addEventListener('submit', this.boundHandleSubmit);
    }

    /**
     * Removes event listeners to prevent memory leaks.
     *
     * @private
     */
    #removeEventListeners() {
      document.removeEventListener('product-overview:variant-change', this.boundHandleVariantChange);
      this.removeEventListener('submit', this.boundHandleSubmit);
    }

    /**
     * Called when the element is connected to the DOM.
     * Initializes the component and fetches initial payment terms.
     */
    connectedCallback() {
      this.productOverview = this.closest('product-overview');

      if (this.allowDebug()) {
        console.log('PaymentTermsContainer: Product overview:', this.productOverview);
      }

      if (!this.productOverview) {
        console.warn('PaymentTermsContainer: No product-overview parent found');
        return;
      }

      // Fetch payment terms if variant is available
      if (this.productOverview.variant?.available_shopify && this.productOverview.variant?.available_greyson) {
        this.fetchPaymentTerms(
          this.productOverview.variant.product_handle,
          this.productOverview.variant.id
        );
      }

      this.#setEventListeners();
    }

    /**
     * Called when the element is disconnected from the DOM.
     * Cleans up event listeners and cancels any pending requests.
     */
    disconnectedCallback() {
      this.#removeEventListeners();

      // Cancel any pending fetch request
      if (this.fetchController) {
        this.fetchController.abort();
        this.fetchController = null;
      }

      // Clear references
      this.productOverview = null;
      this.shopifyPaymentTerms = null;
    }
  }

  customElements.define('payment-terms-container', PaymentTermsContainer);
}
